"use client"

import { ReactNode } from "react"
import Image from "next/image"
import { usePathname } from "next/navigation"
import { Sidebar, SidebarSection, SidebarItem } from "@ui/sidebar"
import { Navbar, NavbarItem, NavbarSection } from "@ui/navbar"
import { StackedLayout } from "@ui/stacked-layout"
import wideLogoImage from "../../domains/layout/images/wide-logo-color.svg"
import { Footer } from "../../themes/saliant/components/Footer"
import { apiDocsUrl } from "./config"

const navItems = [
    { title: "Main Site", href: "/" },
    { title: "Overview", href: "/pulse" },
    { title: "Pricing", href: "/pulse/pricing" },
    { title: "Quickstart", href: "/pulse/quickstart" },
    { title: "SDKs", href: "/pulse/sdks" },
    { title: "Authentication", href: "/pulse/authentication" },
    { title: "Plugins", href: "/pulse/extensions" },
    { title: "API Reference", href: apiDocsUrl },
]

export default function PulseLayout({ children }: { children: ReactNode }) {
    const pathname = usePathname() || ""
    return (
        <StackedLayout
            navbar={
                <Navbar>
                    <NavbarSection>
                        <NavbarItem href="/">
                            <Image className="h-10 w-auto" src={wideLogoImage} priority alt="ResearchWiseAI Logo" />
                        </NavbarItem>
                        <NavbarItem href="/pulse">Pulse</NavbarItem>
                        <NavbarItem href={apiDocsUrl}>API</NavbarItem>
                        <NavbarItem href="/pulse/sdks">SDKs</NavbarItem>
                        <NavbarItem href="/pulse/extensions">Plugins</NavbarItem>
                        <NavbarItem href="/">Main Site</NavbarItem>
                    </NavbarSection>
                </Navbar>
            }
            sidebar={
                <Sidebar>
                    <SidebarSection>
                        {navItems.map((item) => (
                            <SidebarItem key={item.href} href={item.href} current={pathname === item.href}>
                                {item.title}
                            </SidebarItem>
                        ))}
                    </SidebarSection>
                </Sidebar>
            }
        >
            {/* Breadcrumb JSON-LD */}
            <script
                type="application/ld+json"
                suppressHydrationWarning
                dangerouslySetInnerHTML={{
                    __html: JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "BreadcrumbList",
                        itemListElement: (function () {
                            const base =
                                typeof window !== "undefined" ? window.location.origin : "https://researchwiseai.com"
                            const crumbs = [
                                { name: "Home", item: `${base}/` },
                                { name: "Pulse", item: `${base}/pulse` },
                            ]
                            const parts = pathname.split("/").filter(Boolean)
                            if (parts.length > 1) {
                                let acc = ""
                                for (let i = 1; i < parts.length; i++) {
                                    acc += `/${parts[i]}`
                                    crumbs.push({ name: parts[i].replace(/-/g, " "), item: `${base}/pulse${acc}` })
                                }
                            }
                            return crumbs.map((c, idx) => ({
                                "@type": "ListItem",
                                position: idx + 1,
                                name: c.name,
                                item: c.item,
                            }))
                        })(),
                    }),
                }}
            />
            {children}
            <div className="mt-12 lg:mt-16">
                <Footer />
            </div>
        </StackedLayout>
    )
}
